import { useState, useEffect, useCallback } from "react";
import { useRole } from "@/contexts/RoleContext";
import { generateFMTest, publishFMTest, teacherStudents } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  X, Layers, CheckCircle2, Loader2, Clock, Users, ChevronRight, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const SKILLS = ["Addition", "Subtraction", "Multiplication", "Division"];
const SKILL_SYMBOL = { Addition: "+", Subtraction: "−", Multiplication: "×", Division: "÷" };
const DIFFICULTIES = ["Easy", "Medium", "Hard"];
const DIFFICULTY_DESC = { Easy: "L1–L3", Medium: "L4–L6", Hard: "L7–L10" };
const TIMER_RATE = { Easy: 1.5, Medium: 2, Hard: 3 };
const Q_OPTIONS = [5, 10, 15, 20];

function autoSuggestMins(difficulty, numQ) {
  return Math.ceil((TIMER_RATE[difficulty] || 2) * numQ);
}

function buildTitle(skills, difficulty) {
  const today = new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  return `FM Test — ${skills.join(", ")} — ${difficulty} — ${today}`;
}

export default function FMTestGeneratorModal({ gradeNum, onClose }) {
  const { teacherId } = useRole();

  // Form state
  const [selectedSkills, setSelectedSkills] = useState([...SKILLS]);
  const [difficulty, setDifficulty] = useState("Medium");
  const [numQuestions, setNumQuestions] = useState(10);
  const [timerMode, setTimerMode] = useState("auto");
  const [manualMins, setManualMins] = useState(20);
  const [publishMode, setPublishMode] = useState("class"); // "class" | "select"
  const [selectedLearnerIds, setSelectedLearnerIds] = useState([]);
  const [title, setTitle] = useState(() => buildTitle([...SKILLS], "Medium"));

  // Class students
  const [classStudents, setClassStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);

  // Flow state
  const [phase, setPhase] = useState("form"); // "form" | "generating" | "preview" | "publishing" | "done"
  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  const [shortage, setShortage] = useState(null); // {available, requested}
  const [publishedTestId, setPublishedTestId] = useState(null);

  const suggestedMins = autoSuggestMins(difficulty, numQuestions);
  const timerMins = timerMode === "auto" ? suggestedMins : manualMins;

  // Update title when skills/difficulty change
  useEffect(() => {
    setTitle(buildTitle(selectedSkills.length > 0 ? selectedSkills : ["FM"], difficulty));
  }, [selectedSkills, difficulty]);

  // Update suggested timer display reactively
  // (suggestedMins already recomputes on render)

  // Load class students on mount
  useEffect(() => {
    if (!teacherId || !gradeNum) return;
    setStudentsLoading(true);
    teacherStudents(teacherId, gradeNum)
      .then((data) => {
        const students = data.students || data || [];
        setClassStudents(Array.isArray(students) ? students : []);
      })
      .catch(() => setClassStudents([]))
      .finally(() => setStudentsLoading(false));
  }, [teacherId, gradeNum]);

  const toggleSkill = (skill) => {
    setSelectedSkills((prev) =>
      prev.includes(skill) ? prev.filter((s) => s !== skill) : [...prev, skill]
    );
  };

  const toggleLearner = (id) => {
    setSelectedLearnerIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const getLearnerIds = useCallback(() => {
    if (publishMode === "class") {
      return classStudents.map((s) => s.learner_id || s.id);
    }
    return selectedLearnerIds;
  }, [publishMode, classStudents, selectedLearnerIds]);

  const handleGenerate = async (confirmedCount = null) => {
    if (selectedSkills.length === 0) {
      toast.error("Select at least one skill.");
      return;
    }
    if (getLearnerIds().length === 0) {
      toast.error("No students available for this grade.");
      return;
    }

    setPhase("generating");
    try {
      const result = await generateFMTest({
        skills: selectedSkills,
        difficulty,
        num_questions: confirmedCount ?? numQuestions,
        grade: gradeNum,
      });

      if (result.shortage && confirmedCount === null) {
        setShortage({ available: result.available, requested: numQuestions });
        setGeneratedQuestions(result.questions);
        setPhase("preview");
        return;
      }

      setGeneratedQuestions(result.questions);
      setShortage(null);
      setPhase("preview");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate questions");
      setPhase("form");
    }
  };

  const handlePublish = async () => {
    setPhase("publishing");
    const learnerIds = getLearnerIds().filter(Boolean);
    if (learnerIds.length === 0) {
      toast.error("No students to publish to.");
      setPhase("preview");
      return;
    }
    try {
      const result = await publishFMTest({
        teacher_id: teacherId,
        grade: gradeNum,
        title,
        skills: selectedSkills,
        difficulty,
        timer_minutes: timerMins,
        timer_mode: timerMode,
        questions: generatedQuestions,
        learner_ids: learnerIds,
      });
      setPublishedTestId(result.test_id);
      setPhase("done");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to publish test");
      setPhase("preview");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      data-testid="fm-test-modal"
    >
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-fuchsia-100 text-fuchsia-700 flex items-center justify-center">
              <Layers className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-semibold text-slate-900 text-base">Generate FM Test</h2>
              <p className="text-xs text-slate-500">Grade {gradeNum} · Math Foundations</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg text-slate-400 hover:bg-slate-100 flex items-center justify-center transition"
            data-testid="fm-modal-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          {/* ── FORM PHASE ─────────────────────────────────────────────── */}
          {(phase === "form" || phase === "generating") && (
            <>
              {/* Skills */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Module (Skills) <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {SKILLS.map((skill) => {
                    const on = selectedSkills.includes(skill);
                    return (
                      <button
                        key={skill}
                        onClick={() => toggleSkill(skill)}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium transition ${
                          on
                            ? "bg-fuchsia-50 border-fuchsia-400 text-fuchsia-800"
                            : "bg-white border-slate-200 text-slate-600 hover:border-fuchsia-300"
                        }`}
                        data-testid={`skill-toggle-${skill.toLowerCase()}`}
                      >
                        <span className="font-black text-base w-5 text-center">{SKILL_SYMBOL[skill]}</span>
                        {skill}
                        {on && <CheckCircle2 className="w-3.5 h-3.5 text-fuchsia-500 ml-auto" />}
                      </button>
                    );
                  })}
                </div>
                {selectedSkills.length === 0 && (
                  <p className="text-xs text-red-500 mt-1">Select at least one skill</p>
                )}
              </div>

              {/* Difficulty */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Difficulty <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  {DIFFICULTIES.map((d) => (
                    <button
                      key={d}
                      onClick={() => setDifficulty(d)}
                      className={`flex-1 py-2 rounded-xl border text-sm font-semibold transition ${
                        difficulty === d
                          ? "bg-fuchsia-600 text-white border-fuchsia-600"
                          : "bg-white text-slate-600 border-slate-200 hover:border-fuchsia-300"
                      }`}
                      data-testid={`difficulty-${d.toLowerCase()}`}
                    >
                      {d}
                      <span className="block text-[10px] font-normal opacity-70">{DIFFICULTY_DESC[d]}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Number of questions */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Number of Questions <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  {Q_OPTIONS.map((n) => (
                    <button
                      key={n}
                      onClick={() => setNumQuestions(n)}
                      className={`flex-1 py-2 rounded-xl border text-sm font-semibold transition ${
                        numQuestions === n
                          ? "bg-slate-900 text-white border-slate-900"
                          : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                      }`}
                      data-testid={`num-q-${n}`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>

              {/* Timer */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" /> Test Timer <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2 mb-3">
                  <button
                    onClick={() => setTimerMode("auto")}
                    className={`flex-1 py-2 rounded-xl border text-sm font-medium transition ${
                      timerMode === "auto"
                        ? "bg-sky-50 border-sky-400 text-sky-800"
                        : "bg-white text-slate-600 border-slate-200 hover:border-sky-300"
                    }`}
                    data-testid="timer-auto"
                  >
                    Auto-suggest
                  </button>
                  <button
                    onClick={() => setTimerMode("manual")}
                    className={`flex-1 py-2 rounded-xl border text-sm font-medium transition ${
                      timerMode === "manual"
                        ? "bg-sky-50 border-sky-400 text-sky-800"
                        : "bg-white text-slate-600 border-slate-200 hover:border-sky-300"
                    }`}
                    data-testid="timer-manual"
                  >
                    Set manually
                  </button>
                </div>
                {timerMode === "auto" ? (
                  <div className="bg-sky-50 border border-sky-200 rounded-xl px-4 py-2.5 text-sm text-sky-800">
                    Suggested time: <strong>{suggestedMins} minutes</strong>
                    <span className="text-xs text-sky-600 ml-1">
                      ({TIMER_RATE[difficulty]} min × {numQuestions} questions)
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={1}
                      value={manualMins}
                      onChange={(e) => setManualMins(Math.max(1, parseInt(e.target.value, 10) || 1))}
                      className="w-24 border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-300"
                      data-testid="timer-manual-input"
                    />
                    <span className="text-sm text-slate-600">minutes</span>
                  </div>
                )}
              </div>

              {/* Publish to */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5" /> Publish To <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2 mb-3">
                  <button
                    onClick={() => setPublishMode("class")}
                    className={`flex-1 py-2 rounded-xl border text-sm font-medium transition ${
                      publishMode === "class"
                        ? "bg-fuchsia-50 border-fuchsia-400 text-fuchsia-800"
                        : "bg-white text-slate-600 border-slate-200 hover:border-fuchsia-300"
                    }`}
                    data-testid="publish-entire-class"
                  >
                    Entire Class
                    {publishMode === "class" && !studentsLoading && (
                      <span className="text-xs ml-1 opacity-70">({classStudents.length} students)</span>
                    )}
                  </button>
                  <button
                    onClick={() => setPublishMode("select")}
                    className={`flex-1 py-2 rounded-xl border text-sm font-medium transition ${
                      publishMode === "select"
                        ? "bg-fuchsia-50 border-fuchsia-400 text-fuchsia-800"
                        : "bg-white text-slate-600 border-slate-200 hover:border-fuchsia-300"
                    }`}
                    data-testid="publish-select-students"
                  >
                    Select Students
                  </button>
                </div>

                {publishMode === "select" && (
                  <div className="max-h-36 overflow-y-auto rounded-xl border border-slate-200 divide-y divide-slate-100">
                    {studentsLoading ? (
                      <div className="p-3 text-center text-slate-400 text-sm">Loading…</div>
                    ) : classStudents.length === 0 ? (
                      <div className="p-3 text-center text-slate-400 text-sm">No students in Grade {gradeNum}</div>
                    ) : (
                      classStudents.map((s) => {
                        const id = s.learner_id || s.id;
                        const on = selectedLearnerIds.includes(id);
                        return (
                          <label
                            key={id}
                            className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-slate-50 select-none"
                            data-testid={`learner-pick-${id}`}
                          >
                            <input
                              type="checkbox"
                              checked={on}
                              onChange={() => toggleLearner(id)}
                              className="accent-fuchsia-600 w-4 h-4 rounded"
                            />
                            <span className="text-sm text-slate-800 font-medium">{s.name || id}</span>
                            <span className="text-xs text-slate-400 ml-auto">{id}</span>
                          </label>
                        );
                      })
                    )}
                  </div>
                )}
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">
                  Test Title (editable)
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-300"
                  data-testid="test-title-input"
                />
              </div>
            </>
          )}

          {/* ── PREVIEW PHASE ──────────────────────────────────────────── */}
          {phase === "preview" && (
            <div className="space-y-4">
              {/* Shortage warning */}
              {shortage && (
                <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-amber-800">Limited questions available</p>
                    <p className="text-sm text-amber-700 mt-0.5">
                      Only {shortage.available} questions available for this selection.
                      Proceed with {shortage.available} questions?
                    </p>
                  </div>
                </div>
              )}

              {/* Summary card */}
              <Card className="rounded-2xl border border-slate-200 p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">FM Test Summary</span>
                  <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full bg-fuchsia-100 text-fuchsia-700">
                    <Layers className="w-3 h-3" /> FM
                  </span>
                </div>

                <div>
                  <p className="font-semibold text-slate-900 text-base">{title}</p>
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-slate-50 rounded-xl p-3">
                    <p className="text-xs text-slate-500 mb-0.5">Questions</p>
                    <p className="font-bold text-slate-800">{generatedQuestions.length}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3">
                    <p className="text-xs text-slate-500 mb-0.5">Difficulty</p>
                    <p className="font-bold text-slate-800">{difficulty} ({DIFFICULTY_DESC[difficulty]})</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3">
                    <p className="text-xs text-slate-500 mb-0.5">Timer</p>
                    <p className="font-bold text-slate-800 flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" /> {timerMins} min
                      <span className="text-xs text-slate-400 font-normal">({timerMode})</span>
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3">
                    <p className="text-xs text-slate-500 mb-0.5">Publishing To</p>
                    <p className="font-bold text-slate-800">
                      {publishMode === "class"
                        ? `Entire Class (${classStudents.length})`
                        : `${selectedLearnerIds.length} student${selectedLearnerIds.length !== 1 ? "s" : ""}`}
                    </p>
                  </div>
                </div>

                <div>
                  <p className="text-xs text-slate-500 mb-1">Skills included</p>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedSkills.map((sk) => (
                      <span
                        key={sk}
                        className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-fuchsia-50 border border-fuchsia-200 text-fuchsia-700"
                      >
                        <span className="font-black">{SKILL_SYMBOL[sk]}</span> {sk}
                      </span>
                    ))}
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* ── PUBLISHING ─────────────────────────────────────────────── */}
          {phase === "publishing" && (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <Loader2 className="w-10 h-10 text-fuchsia-500 animate-spin" />
              <p className="text-slate-600 font-medium">Publishing FM test…</p>
            </div>
          )}

          {/* ── DONE ───────────────────────────────────────────────────── */}
          {phase === "done" && (
            <div className="flex flex-col items-center justify-center py-10 gap-4 text-center">
              <div className="w-16 h-16 bg-fuchsia-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-fuchsia-600" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900 text-lg">FM Test Published!</h3>
                <p className="text-slate-500 text-sm mt-1">
                  Test assigned to {publishMode === "class" ? classStudents.length : selectedLearnerIds.length} student{publishMode === "class" && classStudents.length !== 1 ? "s" : ""}.
                  Students can see it in "My Tests".
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex-shrink-0 flex justify-between items-center gap-3">
          {phase === "form" || phase === "generating" ? (
            <>
              <Button variant="outline" onClick={onClose} className="rounded-xl" data-testid="fm-modal-cancel">
                Cancel
              </Button>
              <Button
                onClick={() => handleGenerate()}
                disabled={phase === "generating" || selectedSkills.length === 0}
                className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white rounded-xl flex items-center gap-2"
                data-testid="fm-generate-btn"
              >
                {phase === "generating" ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</>
                ) : (
                  <>Preview Test <ChevronRight className="w-4 h-4" /></>
                )}
              </Button>
            </>
          ) : phase === "preview" ? (
            <>
              <Button
                variant="outline"
                onClick={() => { setPhase("form"); setShortage(null); }}
                className="rounded-xl"
                data-testid="fm-back-btn"
              >
                Back
              </Button>
              <Button
                onClick={handlePublish}
                className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white rounded-xl"
                data-testid="fm-publish-btn"
              >
                Publish Test
              </Button>
            </>
          ) : phase === "done" ? (
            <Button
              onClick={onClose}
              className="w-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white rounded-xl"
              data-testid="fm-done-btn"
            >
              Close
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
